import React from 'react';
import PropTypes from 'prop-types';

// Import JS Modules
import { builderDispatch, WebUI, PrismUI } from '../_utils/ui';
import get from 'lodash/get';
import ShelfEvents from '../_shelf-category/flux-events';
import QuantityPricingTools from './quantity-pricing-tools';
import CalendarDates from './calendar-dates';
// Import JSX Modules
import GroupedCategoryBase from './_common/grouped-category-base';
import DeliveryOptionCalculator from '../_utils/_delivery-options/calculator';
import GroupedCategoryChangeEvents from './grouped-category-change-events';
import CalendarStartDateSelector from './calendar-start-date-selector';
import DeliveryOptions from './delivery-options';
import Designer from './designer';
import Filters from './filters';
import ButtonOptionSelectors from './button-option-selectors';
import OptionSelectors from './option-selectors';
import ProductDescription from './product-description';
import ProductImages from './product-images';
import ProductNotAvailable from './product-not-available';
import Pickup from './pickup';
import Shipping from './shipping';
import Breadcrumb from '../_utils/breadcrumb';
import DeliveryOptionsList from '../_utils/_delivery-options/delivery-options-list';
import MobileFullScreen from '../_utils/mobile-full-screen';
import Favouritable from '../_utils/favouritable';
import { inBrowser } from '../_utils/tools/seo-helpers';
import ProductFlags from './product-flags';
import CrossSellCollection from './_common/cross-sell/cross-sell-collection';
import DeliverySection from '../_grouped-category/delivery-section';
import StockAvailability from '../_grouped-category/stock-availability';
import ViewAllModal from './view-all-modal';
import classNames from 'classnames';
import Variants from './variants';
import Quantity from './quantity';
import NoResults from '../_shelf-category/no-results';
import { getViewport } from '../_utils/hoc/with-viewport-size';
import ThemeAndProductImages from './theme-and-product-images';
import RecentProducts from './recent-products';
import { observableEvent } from '../_utils/observable-event';

const isUserLoggedIn = () => {
  if (!inBrowser()) return false;

  return (
    typeof window.App !== 'undefined' &&
    window.App?.order &&
    typeof window.App.order.logged_in === 'function' &&
    window.App.order.logged_in()
  );
};

// Main Section
class GroupedCategory extends GroupedCategoryBase {
  constructor(props) {
    super(props);
    const {
      favourited,
      data: { delivery_options, default_delivery_option: categoryDefaultDeliveryOption },
      selected_delivery_options: selectedPropDeliveryOptions,
      variant,
      storeId: storeIdProps,
    } = this.props;

    this.state = Object.assign(this.state, {
      leftArrowPosition: '20px',
      calendarStartDate: CalendarDates.getDefaultValue().value,
      favourited,
      loggedOutFavourite: null,
      pickupOpen: false,
      shippingOpen: false,
      selectedDeliveryOptionsState:
        selectedPropDeliveryOptions ||
        (categoryDefaultDeliveryOption && String(categoryDefaultDeliveryOption)) ||
        String(delivery_options[0]?.key),
      selectedVariant: variant,
      headerHeight: 171,
      isClient: false,
      loggedIn: isUserLoggedIn(),
      storeId: storeIdProps,
    });
  }

  componentDidMount() {
    super.componentDidMount();
    this.setState({ isClient: true });
    this.builderDispatchToken = builderDispatch.register((payload) => {
      switch (payload.type) {
        case WebUI.LOGIN:
          if (!payload.previous_basket_contents) {
            this.handleReload();
          }
          break;
        case WebUI.CART_MERGED:
          this.handleReload();
          break;
        case ShelfEvents.FAVOURITED:
          this.handleFavourited();
          break;
        case ShelfEvents.LOGGED_OUT_FAVOURITED:
          this.handleLoggedOutFavourited(payload.designCode);
          break;
        case PrismUI.EVENT_QUANTITY_CHANGED:
          this.setState({
            copies: payload.value,
            quantityChanged: true,
          });
          break;
      }
    });

    if (typeof document !== 'undefined') {
      document.addEventListener('click', this.handleClosePickupShipping);
    }

    if (inBrowser()) {
      window.addEventListener('popstate', this.handlePopState);
      window.addEventListener('pageshow', this.handlePageShow);
    }

    this.updateHeaderHeight();
    if (inBrowser()) {
      window.addEventListener('resize', this.updateHeaderHeight);
    }
    const currentProducts = this.getCurrentProducts(),
      { products = [] } = this.props.data,
      [defaultProduct] = currentProducts.length ? currentProducts : products;
    const [selectedTheme] = defaultProduct?.themes || [{}];
    const { selectedVariant } = this.state;
    this.addDefaultProductToCookie(
      defaultProduct,
      selectedTheme,
      selectedVariant,
    );
  }

  componentWillUnmount() {
    if (typeof document !== 'undefined') {
      document.removeEventListener('click', this.handleClosePickupShipping);
    }

    if (inBrowser()) {
      window.removeEventListener('popstate', this.handlePopState);
      window.removeEventListener('pageshow', this.handlePageShow);
      window.removeEventListener('resize', this.updateHeaderHeight);
    }
  }

  getChildContext() {
    const {
        data: {
          description,
          show_prices_table: showPricesTable,
          use_icons_not_text_in_filters: useIconsNotTextInFilters,
          use_icons_in_comparison_fields: useIconsInComparisonFields,
        },
        urls: { locations: locationsUrl },
      } = this.props,
      { validateOptionsChosen } = this.state;

    return {
      description,
      showPricesTable,
      useIconsNotTextInFilters,
      useIconsInComparisonFields,
      validateOptionsChosen,
      locationsUrl,
    };
  }

  render() {
    const {
      data: {
        title,
        filters,
        delivery_options: deliveryOptions,
        breadcrumbs,
        prints_item_page: printsItemPage,
        productInfo,
      },
      design_code: defaultDesignCode,
      features: {
        change_delivery_opts_before_checkout: changeDeliveryOptsBeforeCheckout,
        show_unit_price_on_item_pages: showUnitPriceOnItemPages,
      },
      item_page_insert: itemPageInsert,
      check_stock_availability: checkStockAvailability,
      urls,
      seo_h1: seoH1,
    } = this.props;

    if (deliveryOptions.length < 1) {
      return <NoResults redirection={true} />;
    }

    let {
      data: { products = [] },
    } = this.props;

    const filteredDeliveryOptions = deliveryOptions.filter((option) => {
      const isPresent = products.some((product) =>
        product.delivery_options.includes(option.key),
      );
      return isPresent;
    });

    const { isMobile, isDesktop } = this.state.isClient
      ? getViewport()
      : { isMobile: false, isDesktop: false };

    const variantName = () => {
      if (!selectedTheme) return;
      const { selectedVariant } = this.state;
      const { variants } = selectedTheme;

      if (!variants) return {};

      return selectedVariant in variants
        ? selectedVariant
        : Object.keys(variants).find((key) => variants[key].default);
    };

    if (products.length === 0) {
      return (
        <div className="alert alert-warning" role="alert">
          <strong>Sorry:</strong> No products were found.
        </div>
      );
    }

    const currentProducts = this.getCurrentProducts(),
      [defaultProduct] = currentProducts.length ? currentProducts : products,
      [selectedTheme] = defaultProduct?.themes || [{}];

    this.notifyMissingDataError(defaultProduct, selectedTheme);

    let $priceAndCreateDesktop,
      $priceAndCreateMobile,
      $designer,
      $page,
      currentProduct,
      $itemPageInsert,
      { copies, quantityChanged } = this.state;

    if (defaultProduct.default_quantity && !quantityChanged) {
      copies = defaultProduct.default_quantity;
    }

    if (
      defaultDesignCode &&
      selectedTheme &&
      selectedTheme.designers_names &&
      selectedTheme.designers_names[0]
    ) {
      $designer = (
        <Designer
          name={selectedTheme.designers_names[0]}
          url={breadcrumbs[breadcrumbs.length - 2].url}
        />
      );
    }

    if (currentProducts.length === 1) {
      const { selectedVariant, selectedDeliveryOptionsState } = this.state;
      [currentProduct] = currentProducts;
      currentProduct.price = currentProduct.minimum_price;

      if (
        selectedTheme &&
        selectedTheme.designers_names &&
        selectedTheme.designers_names[0]
      ) {
        $designer = (
          <Designer
            name={selectedTheme.designers_names[0]}
            url={breadcrumbs[breadcrumbs.length - 2].url}
          />
        );
      }

      this.setPageAttributesForProduct(
        currentProduct,
        selectedTheme,
        defaultDesignCode,
        selectedVariant,
        selectedDeliveryOptionsState,
        productInfo,
      );

      $page = this.setInnerHtmlDescription(currentProduct.page_html);

      copies = (() => {
        const quantityPricingTools = new QuantityPricingTools(currentProduct);
        return quantityPricingTools.currentOrFirstAvailableCopies(copies);
      })();

      if (
        get(this.productSelectedAnalyticsSentFor, 'product') !==
          currentProduct.id ||
        get(this.productSelectedAnalyticsSentFor, 'copies') !== copies
      ) {
        builderDispatch.dispatch({
          type: GroupedCategoryChangeEvents.PRODUCT_SELECTED,
          product: currentProduct,
          deliveryOptions,
          breadcrumbs,
          copies,
          printsItemPage,
        });

        this.productSelectedAnalyticsSentFor = {
          product: currentProduct.id,
          copies,
        };
      }
    }

    if (currentProducts.length === products.length) {
      if (!this.landingAnalyticsSent) {
        builderDispatch.dispatch({
          type: GroupedCategoryChangeEvents.PAGE_LOADED,
          product: currentProducts[0],
          deliveryOptions,
          breadcrumbs,
          printsItemPage,
        });

        this.landingAnalyticsSent = true;
      }
    }

    const {
      selectedDeliveryOptionsState,
      calendarStartDate,
      options,
      loggedIn,
    } = this.state;

    $priceAndCreateDesktop = this.renderPriceAndProductCreate(
      currentProducts,
      selectedTheme,
      calendarStartDate,
      copies,
      options,
      variantName(),
      selectedDeliveryOptionsState,
      printsItemPage,
      'desktop',
      showUnitPriceOnItemPages,
    );

    $priceAndCreateMobile = this.renderPriceAndProductCreate(
      currentProducts,
      selectedTheme,
      calendarStartDate,
      copies,
      options,
      variantName(),
      selectedDeliveryOptionsState,
      printsItemPage,
      'mobile',
      showUnitPriceOnItemPages,
    );

    $itemPageInsert = (
      <div dangerouslySetInnerHTML={{ __html: itemPageInsert }} />
    );

    let $deliveryOptions,
      $deliveryOptionsDropdowns = [];
    if (changeDeliveryOptsBeforeCheckout) {
      $deliveryOptions = (
       <DeliveryOptionsList
          options={deliveryOptions}
          product={currentProduct}
          selectedDeliveryOptions={selectedDeliveryOptionsState}
        />
      );
    } else {
      $deliveryOptions = (
        <DeliveryOptions
          options={deliveryOptions}
          products={currentProducts}
          pickupOpen={this.state.pickupOpen}
          shippingOpen={this.state.shippingOpen}
          handleOpenPickup={this.handleOpenPickup}
          handleOpenShipping={this.handleOpenShipping}
        />
      );

      let productsDeliveryOptions = [];
      for (const product of products) {
        productsDeliveryOptions = productsDeliveryOptions.concat(
          product.delivery_options,
        );
      }
      const { hasOneHourPickup } =
        DeliveryOptionCalculator.productsDeliveryOptionsTiers(
          productsDeliveryOptions,
          deliveryOptions,
        );

      $deliveryOptionsDropdowns = [
        <Pickup
          key="pickup"
          deliveryTimes={deliveryTimes(
            (deliveryOption) => deliveryOption.type === 'PICKUP',
          )}
          hasOneHourPickup={hasOneHourPickup}
          open={this.state.pickupOpen}
          disabled={this.state.pickupDisabled}
          leftArrowPosition={this.state.leftArrowPosition}
        />,
        <Shipping
          key="shipping"
          deliveryTimes={deliveryTimes(
            (deliveryOption) => deliveryOption.type !== 'PICKUP',
          )}
          open={this.state.shippingOpen}
          disabled={this.state.shippingDisabled}
          leftArrowPosition={this.state.leftArrowPosition}
        />,
      ];
    }

    const $flags = currentProduct && renderFlag();

    const handleDeliverySelection = (option) => {
      this.setState({
        selectedDeliveryOptionsState: option.toString(),
      });
    };

    const refreshOrder = async () => {
      if (!urls.order_url) return;

      try {
        const latestOrder = await fetch(urls.order_url, {
          credentials: 'same-origin',
        });

        if (!latestOrder.ok) {
          observableEvent.record('refresh-order-fail', { status: latestOrder.status });
          return;
        }

        const orderResponse = await latestOrder.json();
        const pickupId = orderResponse?.pickup_location?.id;
        if (window.App?.order) {
          window.App.order.storeId = pickupId;
        }
        this.setState({ storeId: pickupId });
      } catch (error) {
        observableEvent.record('refresh-order-fail', { message: String(error) });
      }
    };

    const $favoritable = (showButtonText) => {
      if (!printsItemPage) {
        return (
          <Favouritable
            designCode={defaultDesignCode}
            favourited={this.state.favourited}
            showButtonText={showButtonText}
            loggedIn={loggedIn}
          />
        );
      }
    }

    const itemThemeTitle =
      (defaultDesignCode && selectedTheme && selectedTheme.title) || title;

    const breadCrumbTitle = () => {
      // currentProduct is always set on client side, but can be null on server side
      // which is why we need to rely on seoH1 for server side rendered pages to avoid hydration issues.
      // and for consistency as well
      if (currentProduct) {
        return `${currentProduct.title} - ${itemThemeTitle}`;
      } else {
        return seoH1;
      }
    };

    const { selectedVariant, headerHeight } = this.state;
    const priceAndProductCreateStyle = { top: `${headerHeight}px` };
    return (
      <div>
        {/* Breadcrumbs navigation */}
        <div className="navigation-and-title ">
          <Breadcrumb segments={breadcrumbs} title={breadCrumbTitle()} />
        </div>
        {/* mobile page title  */}
        <div className="d-block d-lg-none">
          {this.state.isClient && (
            <div className="heading-flags mb-3">{$flags}</div>
          )}
          <div className="d-flex item-theme-title">
            {itemThemeTitle}
            {this.state.isClient && (
              <div className="d-md-none d-block">{$favoritable()}</div>
            )}
          </div>
          {$designer}
        </div>
        {$page}
        <ViewAllModal
          open={this.state.viewAllImages}
          product={defaultProduct}
          key={`${defaultProduct.id}-${selectedVariant}`}
          selectedTheme={selectedTheme}
          rounded_corners={this.roundOptionActive()}
          scalloped_edges={this.scallopedOptionActive()}
          elegant_corners={this.elegantOptionActive()}
          square_edges={this.squareOptionActive()}
          selectedOptions={options}
          productOptions={currentProduct && currentProduct.options}
          viewAll={this.handleViewAllProductImages}
          selectedVariant={selectedVariant}
          onClose={() => this.handleViewAllProductImages(false)}
        />
        <div
          className="col-12 col-md-3 price-and-product-create d-none d-lg-block"
          style={priceAndProductCreateStyle}>
          <div className="price-and-create-wrapper px-3">
            {$priceAndCreateDesktop}
            <div className="row d-md-block align-self-end">
              <ProductNotAvailable visible={currentProducts.length === 0} />
            </div>
            <div className="delivery-container">
              <DeliverySection
                deliveryOptions={filteredDeliveryOptions}
                selectedDeliveryOptions={
                  this.state.selectedDeliveryOptionsState
                }
                handleDeliveryOptions={handleDeliverySelection}
                products={products}
              />
            </div>
            {checkStockAvailability && isDesktop && (
              <StockAvailability
                copies={this.state.copies}
                currentProducts={currentProducts}
                deliveryOptions={filteredDeliveryOptions}
                selectedDeliveryOption={this.state.selectedDeliveryOptionsState}
                storeId={this.state.storeId}
                urls={urls}
                refreshOrder={refreshOrder}
              />
            )}
            <div className="d-flex flex-row justify-content-center py-2">
              {$favoritable(true)}
            </div>
          </div>
        </div>
        <div className="row">
          <div className="col-12 col-md-6 col-lg-5 product-images d-block d-md-flex flex-column flex-md-row p-0">
            <ProductImages
              product={defaultProduct}
              key={`${defaultProduct.id}-${selectedVariant}`}
              selectedTheme={selectedTheme}
              rounded_corners={this.roundOptionActive()}
              scalloped_edges={this.scallopedOptionActive()}
              elegant_corners={this.elegantOptionActive()}
              square_edges={this.squareOptionActive()}
              selectedOptions={options}
              productOptions={currentProduct && currentProduct.options}
              viewAll={this.handleViewAllProductImages}
              selectedVariant={selectedVariant}
            />
            <div className="d-none d-md-block">{$favoritable()}</div>
          </div>
          <div className="col-12 col-md-6 col-lg-4 product-details mt-5 mt-md-0 mt-lg-0">
            <div className="title-and-flags d-none d-lg-block">
              <div className="heading-flags mb-3">{$flags}</div>
              <p className="item-theme-title">{itemThemeTitle}</p>
              {$designer}
            </div>
            <div className="filters-and-product" ref={this.productAndFilters}>
              <div
                className={classNames('new-card-body filters product', {
                  'border-0': products.length === 1,
                })}>
                <Variants
                  variants={selectedTheme && selectedTheme.variants}
                  selectedVariant={selectedVariant}
                  onVariantSelected={this.handleChangeVariant}
                />
                <div className="delivery-container d-block d-lg-none">
                  <DeliverySection
                    deliveryOptions={filteredDeliveryOptions}
                    selectedDeliveryOptions={
                      this.state.selectedDeliveryOptionsState
                    }
                    handleDeliveryOptions={handleDeliverySelection}
                    products={products}
                  />
                  {checkStockAvailability && isMobile && (
                    <StockAvailability
                      copies={this.state.copies}
                      currentProducts={currentProducts}
                      deliveryOptions={filteredDeliveryOptions}
                      storeId={this.state.storeId}
                      selectedDeliveryOption={
                        this.state.selectedDeliveryOptionsState
                      }
                      urls={urls}
                      refreshOrder={refreshOrder}
                    />
                  )}
                </div>
                <Filters
                  levels={filters}
                  selected={this.state.filters}
                  products={products}
                  currentProducts={currentProducts}
                  deliveryOptions={deliveryOptions}
                  onCriteronSelected={this.handleFilterCriteronSelected}
                  selectedDeliveryOptions={
                    this.state.selectedDeliveryOptionsState
                  }
                />
                {renderCalendarSelector.call(this)}
                {this.props.show_button_for_frames ? (
                  <ButtonOptionSelectors
                    options={currentProduct && currentProduct.options}
                    selected={this.state.options}
                    onChange={this.handleOptionChange}
                  />
                ) : (
                  <OptionSelectors
                    options={currentProduct && currentProduct.options}
                    selected={this.state.options}
                    onChange={this.handleOptionChange}
                  />
                )}
                <div className="product-selection">
                  <div className="price-and-create-mobile d-block d-lg-none">
                    <ProductNotAvailable
                      visible={currentProducts.length === 0}
                    />
                    {$priceAndCreateMobile}
                  </div>
                </div>
                <div>{$itemPageInsert}</div>
                <Quantity
                  products={currentProducts.length ? currentProducts : products}
                  copies={copies}
                  showUnitPriceOnItemPages={showUnitPriceOnItemPages}
                />
              </div>
            </div>
            {this.renderCrossSellCollection(
              currentProducts.length ? currentProducts : products,
              selectedTheme,
            )}
          </div>
        </div>

        <ProductDescription
          currentProduct={currentProduct}
          products={products}
          deliveryOptions={deliveryOptions}
          aiContent={(() => {
            if (!selectedTheme || !selectedTheme.ai_content) return null;
            try {
              const parsed = JSON.parse(selectedTheme.ai_content);
              return parsed.product_description || null;
            } catch (e) {
              return null;
            }
          })()}
        />
        {this.state.isClient && (
          <RecentProducts
            products={
              this.getCookie('recentProducts')
                ? JSON.parse(this.getCookie('recentProducts'))
                : []
            }
            currentProductId={
              currentProduct ? currentProduct.id : defaultProduct.id
            }
            currentThemeTitle={selectedTheme ? selectedTheme.title : ''}
          />
        )}
        <MobileFullScreen
          smallScreenOnly
          open={this.state.pickupOpen}
          onBack={this.handleClosePickupShipping}>
          {$deliveryOptionsDropdowns[0]}
        </MobileFullScreen>
        <MobileFullScreen
          smallScreenOnly
          open={this.state.shippingOpen}
          onBack={this.handleClosePickupShipping}>
          {$deliveryOptionsDropdowns[1]}
        </MobileFullScreen>
      </div>
    );
    function renderFlag() {
      const props = {};

      if (currentProduct) {
        props.prices = currentProduct.prices;
        props.was_price = currentProduct.was_price;
        const productFlag = currentProduct?.flag;
        const themeFlag = selectedTheme?.flag;
        // Assign numbered flags to maskFlag
        const objectFlags = [];

        if (typeof productFlag === 'number' || typeof themeFlag === 'number') {
          props.flag =
            (currentProduct.flag || 0) + ((selectedTheme || {}).flag || 0);
        } else if (typeof themeFlag === 'object') {
          props.flag = [];
          if (themeFlag) props.flag.push(themeFlag);
          if (productFlag) props.flag.push(productFlag);
        }
      }

      return <ProductFlags className="right" {...props} copies={copies} />;
    }

    function renderCalendarSelector() {
      if (defaultProduct.is_calendar && !defaultProduct.hide_start_date) {
        return (
          <CalendarStartDateSelector
            value={this.state.calendarStartDate}
            onChange={this.handleCalendarStartChange}
            isClient={this.state.isClient}
          />
        );
      }
    }

    function deliveryTimes(predicate) {
      const deliveryDays = {};

      for (const currentProduct of currentProducts) {
        for (const deliveryKey of currentProduct.delivery_options) {
          const deliveryOption = deliveryOptions.find(
            (deliveryOpt) => deliveryOpt.key == deliveryKey,
          );
          if (predicate(deliveryOption)) {
            deliveryDays[deliveryOption.type] = deliveryOption.time_hours;
          }
        }
      }

      return deliveryDays;
    }
  }

  updateHeaderHeight = () => {
    if (typeof document === 'undefined') return;
    const mainHeader = document.querySelector('.main-header');
    this.setState({
      headerHeight: mainHeader
        ? mainHeader.getBoundingClientRect().height
        : 171,
    });
  };

  notifyMissingDataError = (
    defaultProduct,
    selectedTheme,
    context = 'Grouped page missing data',
  ) => {
    if (typeof window.newrelic === 'undefined') return;

    if (defaultProduct) {
      if (this.props.data.prints_item_page) return;
      if (selectedTheme) {
        // Check if object has any properties (efficient O(1) check, no array allocation)
        for (const key in selectedTheme) return;
      }
    }

    window.newrelic.noticeError(new Error('Grouped page missing data'), {
      props: this.props,
      context,
    });
  };
  handleChangeVariant = (selectedVariant) => {
    const {
      design_code: designCode,
      data: { productInfo, products },
    } = this.props;
    const { selectedDeliveryOptionsState } = this.state;
    const currentProducts = this.getCurrentProducts(),
      [defaultProduct] = currentProducts.length ? currentProducts : products,
      [selectedTheme] = defaultProduct?.themes || [{}];

    this.notifyMissingDataError(
      defaultProduct,
      selectedTheme,
      'Grouped page missing data - handleChangeVariant',
    );

    const currentProduct =
      currentProducts.length === 1 ? currentProducts[0] : defaultProduct;
    this.setPageAttributesForProduct(
      currentProduct,
      selectedTheme,
      designCode,
      selectedVariant,
      selectedDeliveryOptionsState,
      productInfo,
    );
    this.setState({ selectedVariant });
  };

  handleViewAllProductImages = (state) => {
    this.setState({
      viewAllImages: state,
    });
  };

  getCookie(name) {
    if (typeof document === 'undefined') return null;
    const match = document.cookie.match(
      new RegExp('(^| )' + name + '=([^;]+)'),
    );
    return match ? decodeURIComponent(match[2]) : null;
  }

  setCookie(name, value, days = 365) {
    if (typeof document === 'undefined') return;
    const expires = new Date(
      Date.now() + days * 24 * 60 * 60 * 1000,
    ).toUTCString();
    document.cookie = `${name}=${encodeURIComponent(value)}; expires=${expires}; path=/`;
  }

  addDefaultProductToCookie = (
    defaultProduct,
    selectedTheme,
    selectedVariant,
  ) => {
    if (
      !defaultProduct ||
      typeof document === 'undefined' ||
      this.props.data.prints_item_page
    )
      return;

    let recentProducts = [];
    try {
      const cookieValue = this.getCookie('recentProducts');
      if (cookieValue) {
        recentProducts = JSON.parse(cookieValue);
      }
    } catch (e) {
      recentProducts = [];
    }

    const images = ThemeAndProductImages({
      selectedTheme,
      product: defaultProduct,
      selectedVariant,
    });

    const productData = {
      id: defaultProduct.id,
      title: defaultProduct.title,
      themeTitle:
        selectedTheme && selectedTheme.title ? selectedTheme.title : '',
      url: inBrowser() ? window.location.pathname + window.location.search : '',
      thumbnail: (images || []).filter((i) => i.type === 'image')[0]?.thumbnail,
      unitPrices: defaultProduct.prices.prices.unit_prices,
    };

    // Remove duplicates
    recentProducts = recentProducts.filter(
      (p) =>
        !(p.id === productData.id && p.themeTitle === productData.themeTitle),
    );
    // Add to front
    recentProducts.unshift(productData);

    // Limit to 6 items
    if (recentProducts.length > 6) {
      recentProducts = recentProducts.slice(0, 6);
    }

    // Save back to cookie
    this.setCookie('recentProducts', JSON.stringify(recentProducts));
  };

  async handleReload() {
    const { loggedOutFavourite } = this.state;
    const data = new FormData();
    data.append('design_code', loggedOutFavourite);
    data.append('checked', true);

    // First fetch: update favourite status
    await fetch('/favourites', {
      method: 'PUT',
      credentials: 'same-origin',
      body: data,
    });

    // Second fetch: get updated favourites
    const response = await fetch('/favourites', {
      credentials: 'same-origin',
    });
    const favourites = await response.json();

    this.setState({
      favourited: favourites.includes(this.props.design_code),
      loggedIn: isUserLoggedIn(),
    });
  }

  handleFavourited() {
    this.setState((prevState) => {
      return {
        favourited: !prevState.favourited,
      };
    });
  }

  handleLoggedOutFavourited(loggedOutFavourite) {
    this.setState({ loggedOutFavourite });
  }

  handleCalendarStartChange = (timestamp) => {
    this.setState({
      calendarStartDate: timestamp,
    });
  };

  handleOpenPickup = (element, disabled) => {
    this.setState({
      leftArrowPosition: this.calculateLeftArrowPosition(element),
      pickupOpen: !this.state.pickupOpen,
      pickupDisabled: !!disabled,
      shippingOpen: false,
    });
  };

  handleOpenShipping = (element, disabled) => {
    this.setState({
      leftArrowPosition: this.calculateLeftArrowPosition(element),
      pickupOpen: false,
      shippingOpen: !this.state.shippingOpen,
      shippingDisabled: !!disabled,
    });
  };

  handleClosePickupShipping = (e) => {
    if (!this.state.pickupOpen && !this.state.shippingOpen) return;

    if (!inBrowser() || typeof $ === 'undefined') return;

    if (
      e &&
      $(e.target).closest('.delivery-info, .delivery-box, .mobile-full-screen')
        .length > 0
    ) {
      return;
    }

    this.setState({
      pickupOpen: false,
      shippingOpen: false,
    });
  };

  calculateLeftArrowPosition = (element) => {
    if (!inBrowser() || typeof $ === 'undefined') return '20px';
    // Calculate the left space to position the arrow in the middle of the delivery-box location message
    let $element = $(element),
      $locationMessage = $element
        .closest('.delivery-box')
        .find('.see-locations-message'),
      leftArrowPosition;

    leftArrowPosition =
      $locationMessage.offset().left +
      $locationMessage.width() / 2 -
      $element.closest('.product').offset().left -
      12.7;

    return `${leftArrowPosition}px`;
  };

  renderCrossSellCollection = (products, theme) => {
    const { collections } = this.props.data;

    if (!collections || !products || !theme) {
      return null;
    }

    const collectionToDisplay = collections.filter(
      (collectionProduct) =>
        !products.some(
          (product) => collectionProduct.product_id === product.id,
        ) && // do not display products from the same group
        collectionProduct.thumbnails &&
        collectionProduct.thumbnails.length > 0 &&
        collectionProduct.unit_prices &&
        collectionProduct.unit_prices.length,
    );

    if (collectionToDisplay.length === 0) {
      return null;
    }

    return (
      <CrossSellCollection
        id="crossSellCollection"
        collection={collectionToDisplay}
        slidesToShow={3}
      />
    );
  };

  /* eslint-disable camelcase */
  roundOptionActive() {
    let found_active_round_option = false,
      rounded_corners_option,
      filter;

    for (const selected_filter_key in this.state.filters) {
      rounded_corners_option = null;

      for (const filter of this.props.data.filters) {
        if (
          filter.title === selected_filter_key &&
          !!filter.rounded_corners_option
        ) {
          rounded_corners_option = filter.rounded_corners_option;

          break;
        }
      }

      if (
        !!rounded_corners_option &&
        this.state.filters[selected_filter_key] === rounded_corners_option
      ) {
        found_active_round_option = true;

        break;
      }
    }
    return found_active_round_option;
  }

  scallopedOptionActive() {
    return this.props.data.filters.some(
      (f) =>
        !!f.scalloped_edges_option &&
        f.scalloped_edges_option === this.state.filters[f.title],
    );
  }

  elegantOptionActive() {
    return this.props.data.filters.some(
      (f) =>
        !!f.elegant_corners_option &&
        f.elegant_corners_option === this.state.filters[f.title],
    );
  }

  squareOptionActive() {
    return this.props.data.filters.some(
      (f) =>
        !!f.square_edges_option &&
        f.square_edges_option === this.state.filters[f.title],
    );
  }
}

GroupedCategory.childContextTypes = {
  description: PropTypes.string,
  showPricesTable: PropTypes.bool,
  useIconsNotTextInFilters: PropTypes.bool,
  useIconsInComparisonFields: PropTypes.bool,
  validateOptionsChosen: PropTypes.bool,
  locationsUrl: PropTypes.string,
};

export default GroupedCategory;
